/**
 * In-browser compression for bill attachments that have not been saved yet.
 * Images are re-encoded as JPEG; PDFs are re-rendered page by page as JPEG images,
 * so compressed PDFs keep their look but text is no longer selectable.
 */

const IMAGE_MAX_SIDE_PX = 2000;
const IMAGE_JPEG_QUALITY = 0.7;
const PDF_PAGE_MAX_SIDE_PX = 1700;
const PDF_JPEG_QUALITY = 0.65;
/** Keep the original unless compression saves at least 5%. */
const MIN_SAVINGS_RATIO = 0.95;
/** Files at or under this size are not offered for compression. */
const COMPRESS_MIN_BYTES = 1024 * 1024;

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'bmp'];

function fileExtension(name) {
  const parts = String(name || '').toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
}

function attachmentKind(att) {
  const mime = String(att?.mimeType || att?.file?.type || '').toLowerCase();
  const ext = fileExtension(att?.fileName);
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (IMAGE_MIME_TYPES.includes(mime) || IMAGE_EXTENSIONS.includes(ext)) return 'image';
  return null;
}

function dataUrlToBlob(dataUrl) {
  const [header, base64 = ''] = String(dataUrl).split(',');
  const mime = (header.match(/^data:([^;]+)/) || [])[1] || 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function getSourceBlob(att) {
  if (att?.file instanceof Blob) return att.file;
  if (typeof att?.filePath === 'string' && att.filePath.startsWith('data:')) {
    return dataUrlToBlob(att.filePath);
  }
  return null;
}

/** Already compressed (or not worth compressing); a second pass only lowers quality. */
export function isCompressibleAttachment(att) {
  if (!att || att.compressionDone || !attachmentKind(att)) return false;
  if ((Number(att.fileSize) || 0) <= COMPRESS_MIN_BYTES) return false;
  return att.file instanceof Blob
    || (typeof att.filePath === 'string' && att.filePath.startsWith('data:'));
}

export function hasCompressibleAttachments(attachments) {
  return (attachments || []).some(isCompressibleAttachment);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function canvasToJpegBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode image'))),
      'image/jpeg',
      quality
    );
  });
}

function createWhiteCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  return { canvas, ctx };
}

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

async function compressImageBlob(blob) {
  const img = await loadImage(blob);
  const scale = Math.min(1, IMAGE_MAX_SIDE_PX / Math.max(img.naturalWidth, img.naturalHeight));
  const { canvas, ctx } = createWhiteCanvas(img.naturalWidth * scale, img.naturalHeight * scale);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const out = await canvasToJpegBlob(canvas, IMAGE_JPEG_QUALITY);
  canvas.width = 0;
  canvas.height = 0;
  return out;
}

let pdfLibsPromise = null;

function loadPdfLibs() {
  if (!pdfLibsPromise) {
    // pdf.worker.entry sets window.pdfjsWorker, so pdf.js parses on the main thread
    // and no separate worker file has to be served.
    pdfLibsPromise = Promise.all([
      import(/* webpackChunkName: "pdf-compress" */ 'pdfjs-dist/legacy/build/pdf.worker.entry'),
      import(/* webpackChunkName: "pdf-compress" */ 'pdfjs-dist/legacy/build/pdf'),
      import(/* webpackChunkName: "pdf-compress" */ 'pdf-lib'),
    ]).then(([, pdfjsLib, pdfLib]) => ({
      pdfjsLib,
      PDFDocument: pdfLib.PDFDocument,
    })).catch((err) => {
      pdfLibsPromise = null;
      throw err;
    });
  }
  return pdfLibsPromise;
}

async function compressPdfBlob(blob) {
  const { pdfjsLib, PDFDocument } = await loadPdfLibs();
  const source = await pdfjsLib.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
  try {
    const output = await PDFDocument.create();
    for (let pageNo = 1; pageNo <= source.numPages; pageNo += 1) {
      const page = await source.getPage(pageNo);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.min(2, PDF_PAGE_MAX_SIDE_PX / Math.max(base.width, base.height));
      const viewport = page.getViewport({ scale });
      const { canvas, ctx } = createWhiteCanvas(viewport.width, viewport.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      const jpegBlob = await canvasToJpegBlob(canvas, PDF_JPEG_QUALITY);
      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();

      const jpg = await output.embedJpg(await jpegBlob.arrayBuffer());
      const outPage = output.addPage([base.width, base.height]);
      outPage.drawImage(jpg, { x: 0, y: 0, width: base.width, height: base.height });
    }
    const bytes = await output.save({ useObjectStreams: true });
    return new Blob([bytes], { type: 'application/pdf' });
  } finally {
    await source.destroy();
  }
}

async function compressAttachment(att) {
  const kind = attachmentKind(att);
  const sourceBlob = getSourceBlob(att);
  if (!kind || !sourceBlob) return null;

  const originalSize = Number(att.fileSize) || sourceBlob.size;
  const compressed = kind === 'pdf'
    ? await compressPdfBlob(sourceBlob)
    : await compressImageBlob(sourceBlob);
  if (compressed.size >= originalSize * MIN_SAVINGS_RATIO) return null;

  const isImage = kind === 'image';
  const fileName = isImage
    ? String(att.fileName || 'image').replace(/\.[^.]+$/, '') + '.jpg'
    : att.fileName;
  const mimeType = isImage ? 'image/jpeg' : 'application/pdf';
  const file = new File([compressed], fileName, { type: mimeType });

  return {
    ...att,
    fileName,
    fileSize: file.size,
    mimeType,
    filePath: await blobToDataUrl(file),
    file,
  };
}

/**
 * Returns a replacement for the attachment. The result carries
 * compressionResult: 'done' (with originalFileSize) | 'skipped' | 'failed';
 * failed files stay eligible so the user can retry.
 */
export async function compressSingleAttachment(att) {
  try {
    const smaller = await compressAttachment(att);
    if (smaller) {
      return {
        ...smaller,
        compressionDone: true,
        compressionResult: 'done',
        originalFileSize: Number(att.fileSize) || 0,
      };
    }
    return { ...att, compressionDone: true, compressionResult: 'skipped' };
  } catch (err) {
    return { ...att, compressionResult: 'failed' };
  }
}
